import { useQuery } from '@tanstack/react-query'
import {
  getPlanDetails,
  getPlanVersionLines,
  type PlanItemsResponse,
  type PlanDetails,
} from '@/api/planItems'

/**
 * Hook for fetching paginated plan version lines
 * First fetches plan details to get active_version_id, then fetches lines
 * @param planId - The plan ID to fetch items for (null disables query)
 * @param page - Page number (1-indexed)
 * @param pageSize - Number of items per page
 */
export function usePlanVersionLines(
  planId: string | null,
  page: number = 1,
  pageSize: number = 5
) {
  // First query: get plan details to retrieve active_version_id
  const planDetailsQuery = useQuery<PlanDetails>({
    queryKey: ['planDetails', planId],
    queryFn: () => getPlanDetails(planId!),
    enabled: !!planId,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  })

  const activeVersionId = planDetailsQuery.data?.active_version_id

  // Second query: get version lines using the active_version_id
  const versionLinesQuery = useQuery<PlanItemsResponse>({
    queryKey: ['planVersionLines', planId, activeVersionId, page, pageSize],
    queryFn: () =>
      getPlanVersionLines(planId!, activeVersionId!, page, pageSize),
    enabled: !!planId && !!activeVersionId,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  })

  // Combined loading state: loading if either query is loading
  const isLoading =
    planDetailsQuery.isLoading ||
    (planDetailsQuery.isSuccess && versionLinesQuery.isLoading)

  // Combined error state: error from either query
  const error = planDetailsQuery.error || versionLinesQuery.error

  return {
    data: versionLinesQuery.data,
    isLoading,
    error,
    planDetails: planDetailsQuery.data,
  }
}
